import { useEffect } from "react";
import { isRouteErrorResponse, useNavigate, useRouteError } from "react-router-dom";
import { Button } from "@/components/ui/Button";
import { Card, CardBody } from "@/components/ui/Card";
import { Wordmark } from "@/components/ui/Wordmark";
import { AlertIcon, SearchIcon } from "@/components/ui/icons";

/**
 * What a route shows when it throws, instead of React Router's developer
 * screen.
 *
 * Three cases, told apart by what the person can do about them:
 *
 *   - **The page's code would not download.** A lazy chunk failed — the dev
 *     server stopped, the network dropped mid-navigation, or a deploy replaced
 *     the hashed files this tab still points at. A reload fixes all three, so
 *     that is the one action offered.
 *   - **Not found.** A 404 response thrown by a route.
 *   - **Anything else.** A render bug. Reload or go back to the overview.
 *
 * Like `ConnectionProblem`, it says nothing technical on screen. The error is
 * logged to the console, where the person who can act on it is looking.
 *
 * `fullScreen` is for the top level, where the shell itself may be what broke;
 * inside the shell it renders as a card so the sidebar stays usable.
 */

// Messages differ per browser: Chrome/Edge, Firefox, Safari, and Vite's own
// preload helper.
const CHUNK_ERROR =
  /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|Unable to preload CSS/i;

function isChunkLoadError(error: unknown) {
  return error instanceof Error && CHUNK_ERROR.test(error.message);
}

type Kind = "chunk" | "not-found" | "unknown";

function classify(error: unknown): Kind {
  if (isRouteErrorResponse(error) && error.status === 404) return "not-found";
  if (isChunkLoadError(error)) return "chunk";
  return "unknown";
}

const COPY: Record<Kind, { title: string; body: string }> = {
  chunk: {
    title: "This page couldn't be loaded",
    body: "The panel may have been updated, or the connection dropped while the page was loading. Reloading usually fixes it.",
  },
  "not-found": {
    title: "Page not found",
    body: "That page doesn't exist in the admin panel. It may have moved, or the link may be wrong.",
  },
  unknown: {
    title: "Something went wrong",
    body: "This page ran into a problem. Nothing you've saved has been lost — try reloading, or head back to the overview.",
  },
};

export function RouteError({ fullScreen = false }: { fullScreen?: boolean }) {
  const error = useRouteError();
  const kind = classify(error);
  const { title, body } = COPY[kind];

  useEffect(() => {
    if (kind !== "not-found") console.error("[route error]", error);
  }, [error, kind]);

  const content = (
    <>
      <span
        aria-hidden="true"
        className={
          kind === "not-found"
            ? "bg-surface-sunken text-content-subtle mx-auto flex size-12 items-center justify-center rounded-full"
            : "bg-warning-soft text-warning mx-auto flex size-12 items-center justify-center rounded-full"
        }
      >
        {kind === "not-found" ? (
          <SearchIcon className="size-5" />
        ) : (
          <AlertIcon className="size-5" />
        )}
      </span>

      <h1 className="text-content mt-5 text-lg font-semibold tracking-tight">{title}</h1>

      <p className="text-content-muted mx-auto mt-2.5 max-w-md text-sm leading-relaxed">{body}</p>

      <div className="mt-7 flex flex-wrap items-center justify-center gap-3">
        {kind !== "not-found" && (
          <Button onClick={() => window.location.reload()}>Reload page</Button>
        )}
        {/* A full navigation rather than the router's: if the router is what
            broke, a fresh page load is the reliable way out. */}
        <Button
          variant={kind === "not-found" ? "primary" : "secondary"}
          onClick={() => window.location.assign("/")}
        >
          Back to overview
        </Button>
      </div>
    </>
  );

  if (fullScreen) {
    return (
      <div className="bg-canvas flex min-h-dvh flex-col items-center justify-center px-6 text-center">
        <Wordmark className="mb-10" />
        {content}
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-8">
      <Card>
        <CardBody className="px-6 py-14 text-center sm:px-10">{content}</CardBody>
      </Card>
    </div>
  );
}

/** The catch-all route inside the shell. Same look as a thrown 404. */
export function NotFoundPage() {
  const navigate = useNavigate();
  return (
    <div className="p-4 sm:p-8">
      <Card>
        <CardBody className="px-6 py-14 text-center sm:px-10">
          <span
            aria-hidden="true"
            className="bg-surface-sunken text-content-subtle mx-auto flex size-12 items-center justify-center rounded-full"
          >
            <SearchIcon className="size-5" />
          </span>
          <h1 className="text-content mt-5 text-lg font-semibold tracking-tight">
            {COPY["not-found"].title}
          </h1>
          <p className="text-content-muted mx-auto mt-2.5 max-w-md text-sm leading-relaxed">
            {COPY["not-found"].body}
          </p>
          <div className="mt-7 flex justify-center">
            <Button onClick={() => void navigate("/")}>Back to overview</Button>
          </div>
        </CardBody>
      </Card>
    </div>
  );
}
